"use client";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useCommunitiesListingQuery } from "@/hooks/queries/useCommunitiesListingQuery";
import { useCommunityContext } from "@/providers/community-provider";
import { TCommunityJoinedListingRecord, TCommunityListingRecord } from "@/types/community";
import { ChevronsUpDownIcon, PlusIcon } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useState } from "react";

const CommunitiesMenu = ({ name, logo }: { name?: string; logo?: string }) => {
	const [open, setOpen] = useState(false);

	const { communityInfo, isPublic } = useCommunityContext();
	const communitySlug = communityInfo?.slug;

	const { data, isLoading } = useCommunitiesListingQuery(communitySlug!, {
		enabled: !!communitySlug,
	});

	const joined =
		(data?.data?.find((item) => item.title === "Joined")
			?.data as TCommunityJoinedListingRecord[]) || [];

	const communities =
		(data?.data?.find((item) => item.title === "Communities")?.data as TCommunityListingRecord[]) ||
		[];

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger
				disabled={isLoading}
				className="rounded-lg px-2 py-px hover:bg-gray-100 disabled:cursor-not-allowed"
			>
				{isPublic || (!name && !logo) ? (
					<div className="flex items-center justify-start gap-2">
						<Image
							className="h-5 w-auto object-contain"
							src={logo || "/images/communities/discover-communities.svg"}
							alt={name || "Discover Communities"}
							width={1080}
							height={1080}
						/>
						<ChevronsUpDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
					</div>
				) : (
					<div
						role="combobox"
						aria-expanded={open}
						aria-controls="combobox-listbox"
						className="flex items-center justify-start gap-2"
					>
						<Image
							className="h-10 w-10 rounded-lg shadow"
							src={logo || "/images/default-placeholder-image.png"}
							alt={name || "Community"}
							width={1080}
							height={1080}
						/>

						<h1 className="text-sm font-bold md:text-base">{name}</h1>

						<ChevronsUpDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
					</div>
				)}
			</PopoverTrigger>

			<PopoverContent align="start" alignOffset={-15} className="w-96 bg-white p-0 h-[90vh]">
				<Command >
					<CommandInput placeholder="Find Community" />
					<CommandList className="min-h-full">
						<CommandGroup heading="Joined Communities" >
							<CommandEmpty>No communities found.</CommandEmpty>
							{joined?.map((community) => (
								<CommandItem key={community.id}>
									<Link className="flex items-center gap-2" href={`/communities/${community.slug}`}>
										<Avatar className="h-8 w-8 rounded">
											<AvatarImage src={community.logo} />
											<AvatarFallback>{community.name?.slice(0, 1)}</AvatarFallback>
										</Avatar>
										{community.name}
									</Link>
								</CommandItem>
							))}
						</CommandGroup>

						<CommandGroup heading="Communities">
							<CommandEmpty>No communities found.</CommandEmpty>
							{communities?.map((community) => (
								<CommandItem key={community.id}>
									<Link className="flex items-center gap-2" href={`/communities/${community.slug}`}>
										<Avatar className="h-8 w-8 rounded">
											<AvatarImage src={community.logo} />
											<AvatarFallback>{community.name?.slice(0, 1)}</AvatarFallback>
										</Avatar>
										{community.name}
									</Link>
								</CommandItem>
							))}
						</CommandGroup>

						<CommandGroup>
							<CommandItem>
								<Link href="/contact-us" className="flex items-center gap-2">
									<PlusIcon className="h-4 w-4" />
									Create Community
								</Link>
							</CommandItem>
						</CommandGroup>
					</CommandList>
				</Command>
			</PopoverContent>
		</Popover>
	);
};

export default CommunitiesMenu;
